import Head from 'next/head';
import { useRouter } from 'next/router';
import { Provider, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { store, wrapper, IRootState } from 'ui/store';
import { setIsLargeScreen, setIsMobileScreen, setScreenSize } from 'ui/store/page/actions';
import screenBreakpoints from 'ui/constants/screen-breakpoints';
import favicon from '../../public/favicon.ico';
import { montserratCustom } from 'ui/helpers/utils/fonts';
import '../../../../packages/shared/ui/shared-global.css';
import '@/styles/globals.css';
import { setIsGTMReady } from 'ui/store/googleTagManager/actions';
import TagManager from 'react-gtm-module';
import { trackViewPopUp, trackSelectPopUp } from 'ui/lib/gtm-es/pop-up-banner/trackPopUpBanner';

function App({ Component, pageProps }) {
  const { screenSize } = useSelector((state: any) => state.page);
  const { province, city } = useSelector((state: IRootState) => state.geolocation) || {};
  const [urlPage, setUrlPage] = useState('');
  const [isInterracted, setIsInterracted] = useState(false);

  const route = useRouter();

  useEffect(() => {
    store.dispatch(setIsMobileScreen(screenSize?.width < screenBreakpoints.MIN_DESKTOP_SCREEN));
    store.dispatch(setIsLargeScreen(screenSize?.width >= screenBreakpoints.MIN_LARGE_SCREEN));
  }, [screenSize?.width]);

  useEffect(() => {
    if (typeof window !== 'undefined' && route?.asPath) {
      setUrlPage(window.location.origin + window.location.pathname);
    }
  }, [route]);

  useEffect(() => {
    store.dispatch(
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
        mobile: window.innerWidth < screenBreakpoints.MIN_DESKTOP_SCREEN,
      })
    );

    window.addEventListener('resize', (event: any) => {
      store.dispatch(
        setScreenSize({
          width: event.target.innerWidth,
          height: event.target.innerHeight,
          mobile: window.innerWidth < screenBreakpoints.MIN_DESKTOP_SCREEN,
        })
      );
    });

    return () => {
      window.removeEventListener('resize', () => {});
    };
  }, []);

  function handleInteract() {
    setIsInterracted(true);
    store.dispatch(setIsGTMReady(true));
  }

  useEffect(() => {
    window.addEventListener('mousemove', handleInteract);
    window.addEventListener('touchstart', handleInteract);
    return () => {
      window.removeEventListener('mousemove', handleInteract);
      window.removeEventListener('touchstart', handleInteract);
    };
  }, []);

  // Observe popup changes
  const PROMO_SELECTOR = '.popup-overlay';

  useEffect(() => {
    const pushView = (el: HTMLElement) => {
      if (el.getAttribute('data-ga4-view-sent') === '1') return;
      trackViewPopUp({
        creative_name: el.getAttribute('data-creative-name') || 'not available',
        creative_slot: el.getAttribute('data-creative-slot') || 'not available',
        promotion_id: el.getAttribute('data-promotion-id') || 'not available',
        promotion_name: el.getAttribute('data-promotion-name') || 'not available',
      });
      el.setAttribute('data-ga4-view-sent', '1');
    };

    const bindClick = (el: HTMLElement) => {
      const btn = el.querySelector('button.sign-up-cta');
      if (!btn || btn.getAttribute('data-ga4-click-bound')) return;
      btn.setAttribute('data-ga4-click-bound', '1');
      btn.addEventListener('click', () => {
        trackSelectPopUp({
          creative_name: el.getAttribute('data-creative-name') || 'not available',
          creative_slot: el.getAttribute('data-creative-slot') || 'not available',
          promotion_id: el.getAttribute('data-promotion-id') || 'not available',
          promotion_name: el.getAttribute('data-promotion-name') || 'not available',
        });
      });
    };

    const isVisible = (el: HTMLElement) => {
      return (
        el.classList.contains('show-popup') ||
        !!el.closest('.show-popup') ||
        getComputedStyle(el).display !== 'none'
      );
    };

    const tick = () => {
      const el = document.querySelector(PROMO_SELECTOR) as HTMLElement | null;
      if (!el) return;
      if (isVisible(el)) {
        pushView(el);
        bindClick(el);
      }
    };

    const mo = new MutationObserver(tick);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    tick();

    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    const userData =
      localStorage.getItem('userData') !== 'null' &&
      localStorage.getItem('userData') &&
      localStorage.getItem('userData') !== 'undefined'
        ? JSON.parse(localStorage.getItem('userData'))
        : null;
    const tagManagerArgs = {
      gtmId: process.env.NEXT_PUBLIC_GTM_ID,
      auth: process.env.NEXT_PUBLIC_GTM_AUTH,
      preview: process.env.NEXT_PUBLIC_GTM_PREVIEW,
      ...(userData && {
        dataLayer: {
          event: 'userInfo',
          userId: userData.customerNo,
          user_id: userData.customerNo,
          phoneNumber: userData.phoneNumber,
          email: userData.email?.toLowerCase(),
          province: province || 'tidak tersedia',
          city: city || 'tidak tersedia',
        },
      }),
    };

    if (isInterracted) {
      TagManager.initialize(tagManagerArgs);
    }
  }, [isInterracted]);

  return (
    <>
      <Head>
        <link rel="shortcut icon" href={favicon.src} type="image/x-icon" />
        {urlPage && <link rel="canonical" href={urlPage} />}
      </Head>
      <Provider store={store}>
        <main className={montserratCustom.className}>
          <Component {...pageProps} />
        </main>
      </Provider>
    </>
  );
}

export default wrapper.withRedux(App);
